import { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchSkillMatrixFilters } from "../../../../js/features/ManageWorkers/ManageWorkersSlice";
import {
	FILTER_LOCATION_KEY,
	FILTER_WORK_CENTERS_KEY,
} from "../../../assets/constants";
import { LOCATIONS, WORK_CENTERS } from "../../filters/constants";
import { Filter } from "../../filters/filter-accordion/types";
import { FilterContext } from "../../filters/filter-context";

// Initialises the shared filter definitions (Location, Work Center) for the
// Cross Skilling Summary tab. Reuses the skill-matrix filter options already
// in the store (fetchSkillMatrixFilters), including the location→work-center
// cascade relationships. Must be rendered inside a FilterProvider.
const useSummaryFilters = () => {
	const dispatch = useDispatch();

	const allLocationsData = useSelector(
		(state: any) => state.manageWorkers?.skillMatrixFiltersLocations ?? []
	);
	const allWorkCentersData = useSelector(
		(state: any) => state.manageWorkers?.skillMatrixFiltersWorkCenters ?? []
	);
	const filterRelationshipsData = useSelector(
		(state: any) =>
			state.manageWorkers?.skillMatrixFiltersFilterRelationships ?? []
	);
	const skillMatrixFiltersStatus = useSelector(
		(state: any) => state.manageWorkers?.fetchSkillMatrixFilters?.status || "idle"
	);

	const filterContext = useContext(FilterContext);
	if (!filterContext) {
		throw new Error("useSummaryFilters must be used within a FilterProvider");
	}
	const { setFilters, setDataRelations, setIsLoading } = filterContext;

	// The matrix tab dispatches the same fetch on its mount, so only fetch
	// when the options are not already loaded or loading.
	useEffect(() => {
		if (
			skillMatrixFiltersStatus !== "fulfilled" &&
			skillMatrixFiltersStatus !== "pending"
		) {
			dispatch(fetchSkillMatrixFilters());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	useEffect(() => {
		if (skillMatrixFiltersStatus === "pending") {
			setIsLoading?.(true);
			return;
		}
		if (skillMatrixFiltersStatus !== "fulfilled") {
			setIsLoading?.(false);
			return;
		}
		setIsLoading?.(false);

		const newFilters: Filter[] = [
			{
				id: 1,
				name: LOCATIONS,
				key: FILTER_LOCATION_KEY,
				data: allLocationsData,
				reducedData: allLocationsData,
				searchedData: allLocationsData,
			},
			{
				id: 2,
				name: WORK_CENTERS,
				key: FILTER_WORK_CENTERS_KEY,
				data: allWorkCentersData,
				reducedData: allWorkCentersData,
				searchedData: allWorkCentersData,
			},
		];

		setFilters(newFilters);
		setDataRelations(filterRelationshipsData);
	}, [
		skillMatrixFiltersStatus,
		allLocationsData,
		allWorkCentersData,
		filterRelationshipsData,
		setFilters,
		setDataRelations,
		setIsLoading,
	]);
};

export default useSummaryFilters;
